import {
  Box,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead
} from '@mui/material';
import { StyledTableCell, StyledTableRow } from '@components/StyledTable';
import { ArrElement } from '@utils/types';
import {
  FetchMiniClusterQuery,
  Maybe,
  MiniClusterSearchType,
  useFetchMiniClusterQuery
} from '@generated/graphql';
import useQueryString from '@hooks/useQueryString';
import { useState } from 'react';
import { DetailDrawer } from '@components/DetailDrawer';

export type MiniClusterData = ArrElement<
  NonNullable<FetchMiniClusterQuery['fetchMiniCluster']['data']>['icxModelList']
>;

type MiniClusterParamsType = {
  page: string;
  q: string;
  searchType: Maybe<MiniClusterSearchType>;
};

const LIMIT = 14;

const MiniCluster = () => {
  const { onUpdateQueryString, query } =
    useQueryString<MiniClusterParamsType>();

  const [drawer, setDrawer] = useState<{
    open: boolean;
    data?: MiniClusterData;
  }>({
    open: false
  });
  const page = Number(query.page ?? 1);

  const { data, loading, error } = useFetchMiniClusterQuery({
    variables: {
      input: {
        keyword: query.q,
        limit: LIMIT,
        offset: (page - 1) * LIMIT,
        miniClusterSearchType: query.searchType
      }
    }
  });

  if (loading || error) {
    return null;
  }

  const handleOpenDetailPage = (
    id: string | null | undefined,
    data: MiniClusterData
  ) => {
    if (!id) {
      return;
    }

    setDrawer({ open: true, data });
  };

  const miniClusterList = data?.fetchMiniCluster.data?.icxModelList ?? [];
  const miniClustersTotalCount = data?.fetchMiniCluster.data?.count ?? 0;

  return (
    <>
      <TableContainer component={Paper} sx={{ minWidth: 800 }}>
        <Table>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>지역</StyledTableCell>
              <StyledTableCell>거점</StyledTableCell>
              <StyledTableCell>미니클러스트명</StyledTableCell>
              <StyledTableCell>기업유형</StyledTableCell>
              <StyledTableCell>기업명</StyledTableCell>
              <StyledTableCell>산학연 구분</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {miniClusterList.map((miniCluster) => (
              <StyledTableRow
                onClick={() =>
                  handleOpenDetailPage(miniCluster.id, miniCluster)
                }
                key={miniCluster.id}
                sx={{
                  '&:hover': {
                    cursor: 'pointer',
                    backgroundColor: '#00000030'
                  }
                }}
              >
                <StyledTableCell>{miniCluster.region}</StyledTableCell>
                <StyledTableCell>{miniCluster.base}</StyledTableCell>
                <StyledTableCell>{miniCluster.miniClusterName}</StyledTableCell>
                <StyledTableCell>{miniCluster.enterpriseType}</StyledTableCell>
                <StyledTableCell>{miniCluster.companyName}</StyledTableCell>
                <StyledTableCell>
                  {miniCluster.industryUniversityInstituteCollaboration}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        padding={2}
        width={'100%'}
        display={'flex'}
        justifyContent={'center'}
      >
        <Pagination
          count={Math.ceil(miniClustersTotalCount / LIMIT)}
          page={page}
          color={'primary'}
          onChange={(e, value) => {
            onUpdateQueryString({ page: `${value}` });
          }}
        />
        <DetailDrawer
          open={drawer.open}
          onClose={() => setDrawer({ open: false })}
          detailInfo={drawer.data}
          tables={[
            {
              title: '기본 정보',
              table: DefaultTable
            }
          ]}
        />
      </Box>
    </>
  );
};

export default MiniCluster;

const DefaultTable = {
  region: '지역',
  base: '거점',
  miniClusterName: '미니클러스터명',
  enterpriseType: '기업유형',
  companyName: '사업자명',
  businessRegistrationNumber: '사업자번호',
  industryUniversityInstituteCollaboration: '산학연 구분',
  companyRegistrationDate: '기업등록일자',
  memberName: '회원명',
  mailAddress: '메일주소',
  registrationDate: '회원가입일자',
  contact: '연락처'
};
